/**
 * @fileoverview Estilos da triagem no painel do contato (mesmos tons do
 * ContactDrawer). Monocromático; cor só com significado: o estado de cada item
 * e da triagem, nos tons do WhatsApp, o canal da triagem (verde do WhatsApp Web
 * para o que chegou, azul do "lido" para o que a IA está lendo), âmbar para o
 * que espera o cliente e vermelho para o que não serviu. Sem bordas de
 * destaque: o dado recolhido é só rótulo pequeno em cima e valor embaixo.
 */

import { makeStyles } from "@material-ui/core/styles";

/** "#rrggbb" + opacidade → rgba (cor translúcida dos selos e realces). */
export const tint = (hex, alpha) => {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
};

/** Tons de estado (ver helpers: qual estado usa qual tom). */
export const TONES = ["ok", "info", "wait", "bad", "neutral"];

export const tokens = (dark) => ({
  // Cartões no escuro: cinza neutro, da mesma família do painel (#0A0A0A → #18181B).
  surface: dark ? "#1c1e23" : "#ffffff",
  subtle: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.025)",
  hover: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.045)",
  border: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.1)",
  inputBorder: dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.18)",
  textPrimary: dark ? "rgba(255,255,255,0.92)" : "#0f172a",
  textSecondary: dark ? "rgba(255,255,255,0.64)" : "#475569",
  // Tom mais fraco ainda legível no claro (contraste > 4.5 no branco).
  textMuted: dark ? "rgba(255,255,255,0.45)" : "#64748b",
  track: dark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)",
  fill: dark ? "rgba(255,255,255,0.85)" : "#0f172a",
  warn: dark ? "#fbbf24" : "#b45309",
  warnBg: dark ? "rgba(251,191,36,0.1)" : "rgba(180,83,9,0.07)",
  warnBorder: dark ? "rgba(251,191,36,0.28)" : "rgba(180,83,9,0.25)",
  danger: dark ? "#fca5a5" : "#dc2626",
  dangerBorder: dark ? "rgba(239,68,68,0.3)" : "rgba(220,38,38,0.25)",
  // Estados. No claro, tons escuros o bastante para texto: contraste ≥ 4.5 também
  // no selo (fundo com o próprio tom a 8%) e com a linha da tabela em destaque.
  ok: dark ? "#00a884" : "#00715c",
  info: dark ? "#53bdeb" : "#0369a1",
  wait: dark ? "#fbbf24" : "#a3480b",
  bad: dark ? "#f15c6d" : "#c62828",
  neutral: dark ? "rgba(255,255,255,0.45)" : "#64748b",
});

const useTriageStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const t = "all 160ms cubic-bezier(0.4, 0, 0.2, 1)";

  return {
    root: { display: "flex", flexDirection: "column", gap: 12, color: c.textPrimary },
    muted: { color: c.textMuted, fontSize: 12 },
    empty: { fontSize: 13, color: c.textSecondary, lineHeight: 1.5, padding: "4px 2px" },

    // Problema do cliente (topo da aba): o código como no visor do aparelho, no
    // tom de "não serviu" (é um defeito), e o defeito confirmado ao lado.
    problem: {
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      background: c.surface,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    problemTitle: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: c.textMuted,
    },
    problemBody: { display: "flex", alignItems: "flex-start", gap: 12 },
    problemCodes: { display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 },
    problemCode: {
      minWidth: 52,
      padding: "6px 10px",
      borderRadius: 8,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: "0.06em",
      textAlign: "center",
      fontVariantNumeric: "tabular-nums",
      color: c.bad,
      background: tint(c.bad, dark ? 0.12 : 0.07),
      border: `1px solid ${tint(c.bad, dark ? 0.32 : 0.28)}`,
    },
    problemText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 },
    problemDesc: {
      margin: 0,
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.45,
      color: c.textPrimary,
      overflowWrap: "anywhere",
      // Vídeo descrito pela IA pode ser longo: o texto inteiro está no item, abaixo.
      display: "-webkit-box",
      WebkitLineClamp: 4,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    problemItem: {
      display: "block",
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: c.textMuted,
      marginBottom: 2,
    },
    problemMeta: { fontSize: 11.5, lineHeight: 1.4, color: c.textMuted },

    // Cabeçalho da sessão
    card: {
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: 12,
      background: c.surface,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    headRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 },
    title: { fontSize: 13.5, fontWeight: 600, color: c.textPrimary, lineHeight: 1.35, wordBreak: "break-word" },
    subtitle: { fontSize: 11.5, color: c.textMuted, marginTop: 2 },
    status: {
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: 999,
      border: `1px solid ${c.border}`,
      color: c.textSecondary,
      whiteSpace: "nowrap",
    },
    progressLine: { display: "flex", alignItems: "center", gap: 10 },
    progressTrack: { flex: 1, height: 6, borderRadius: 3, background: c.track, overflow: "hidden" },
    // Parte recebida: verde (o mesmo do item recebido).
    progressFill: { height: "100%", borderRadius: 3, background: c.ok, transition: "width 240ms ease" },
    progressText: { fontSize: 12, color: c.textSecondary, whiteSpace: "nowrap" },
    aiLine: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: c.textSecondary },
    attention: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 8,
      fontSize: 12,
      lineHeight: 1.4,
      color: c.warn,
      background: c.warnBg,
      border: `1px solid ${c.warnBorder}`,
    },

    // Botões (mesmo desenho das pílulas do painel do contato)
    actions: { display: "flex", flexWrap: "wrap", gap: 6 },
    btn: {
      flex: "1 1 calc(33% - 6px)",
      minWidth: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "7px 10px",
      borderRadius: 10,
      border: `1px solid ${c.inputBorder}`,
      background: "transparent",
      color: c.textPrimary,
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      transition: t,
      whiteSpace: "nowrap",
      fontFamily: "inherit",
      "&:hover:not(:disabled)": { background: c.hover },
      "&:disabled": { opacity: 0.5, cursor: "default" },
      "& svg": { width: 14, height: 14, strokeWidth: 1.75, flexShrink: 0 },
    },
    btnDanger: {
      color: c.danger,
      borderColor: c.dangerBorder,
    },
    btnPrimary: {
      background: c.fill,
      borderColor: c.fill,
      color: dark ? "#0f172a" : "#ffffff",
      fontWeight: 600,
      "&:hover:not(:disabled)": { background: c.fill, opacity: 0.9 },
    },

    // Iniciar
    startBox: { display: "flex", flexDirection: "column", gap: 8 },
    // Select padrão do app (ModernSelect): aqui só o tamanho das letras.
    select: { width: "100%", "& .MuiInputBase-root": { fontSize: 13 } },

    // Seções
    sectionTitle: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: c.textMuted,
      margin: "4px 2px 0",
    },

    // Itens
    items: { display: "flex", flexDirection: "column", gap: 8 },
    item: {
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: "11px 12px",
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      background: c.surface,
    },
    itemAttention: { borderColor: tint(c.bad, dark ? 0.45 : 0.35) },
    itemIcon: {
      marginTop: 0,
      flexShrink: 0,
      display: "inline-flex",
      color: c.neutral,
      "& svg": { width: 18, height: 18, strokeWidth: 2 },
    },
    itemBody: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
    itemTop: { display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between", minHeight: 18 },
    itemName: { fontSize: 13, fontWeight: 600, color: c.textPrimary, lineHeight: 1.35, wordBreak: "break-word" },
    itemNameDone: { color: c.textSecondary, fontWeight: 500 },
    itemMeta: { fontSize: 11.5, color: c.textMuted, lineHeight: 1.45 },
    metaPart: { whiteSpace: "nowrap" },
    // O estado por escrito; colorido e forte só quando pede algo (espera, leitura, problema).
    itemStatus: { fontSize: 11.5, fontWeight: 500, color: c.textSecondary },
    itemStatusStrong: { fontWeight: 600 },
    itemDetail: { fontSize: 12, color: c.textSecondary, lineHeight: 1.45, wordBreak: "break-word", marginTop: 4 },
    // Dado recolhido: rótulo pequeno em cima e valor embaixo, em colunas
    // quando cabe (painel expandido), uma embaixo da outra quando não.
    answer: {
      marginTop: 8,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))",
      gap: "8px 16px",
    },
    answerField: { minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    answerLabel: {
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: c.textMuted,
    },
    answerValue: { fontSize: 13, color: c.textPrimary, lineHeight: 1.45, wordBreak: "break-word" },
    answerConfidence: { fontSize: 11, fontWeight: 500, color: c.textMuted, marginLeft: 6, whiteSpace: "nowrap" },
    // Anexos do item (miniaturas das fotos e botão do PDF)
    evidenceRow: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 },
    evidenceThumb: {
      width: 52,
      height: 52,
      padding: 0,
      borderRadius: 8,
      border: `1px solid ${c.inputBorder}`,
      background: c.subtle,
      overflow: "hidden",
      cursor: "pointer",
      transition: t,
      "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
      "&:hover": { borderColor: c.textSecondary },
      "&:focus-visible": { outline: `2px solid ${c.textPrimary}`, outlineOffset: 2 },
    },
    evidenceDoc: {
      height: 28,
      padding: "0 10px",
      borderRadius: 8,
      border: `1px solid ${c.inputBorder}`,
      background: "transparent",
      color: c.textPrimary,
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      transition: t,
      "& svg": { width: 14, height: 14 },
      "&:hover": { background: c.hover },
      "&:focus-visible": { outline: `2px solid ${c.textPrimary}`, outlineOffset: 2 },
    },
    // Depois de answerValue: com a mesma especificidade, a ordem decide.
    answerValueInvalid: { color: c.bad, fontWeight: 600 },
    menuBtn: {
      padding: 4,
      marginTop: -3,
      marginRight: -4,
      color: c.textSecondary,
      "& svg": { width: 16, height: 16 },
    },

    // Linha do tempo
    timelineToggle: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      background: "transparent",
      border: "none",
      padding: "2px",
      cursor: "pointer",
      color: c.textMuted,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      fontFamily: "inherit",
      "& svg": { width: 14, height: 14 },
      "&:hover": { color: c.textSecondary },
    },
    timeline: { display: "flex", flexDirection: "column", gap: 0, paddingLeft: 4 },
    event: {
      display: "flex",
      gap: 10,
      position: "relative",
      paddingBottom: 10,
      "&:last-child": { paddingBottom: 0 },
    },
    eventDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      background: c.neutral,
      marginTop: 5,
      flexShrink: 0,
      position: "relative",
      zIndex: 1,
    },
    eventLine: {
      position: "absolute",
      left: 3,
      top: 12,
      bottom: 0,
      width: 1,
      background: c.border,
    },
    eventBody: { minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    eventTitle: { fontSize: 12, color: c.textPrimary, lineHeight: 1.4, wordBreak: "break-word" },
    eventMeta: { fontSize: 11, color: c.textMuted },

    // Histórico
    historyItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "9px 10px",
      borderRadius: 10,
      border: `1px solid ${c.border}`,
      background: "transparent",
      cursor: "pointer",
      transition: t,
      width: "100%",
      textAlign: "left",
      fontFamily: "inherit",
      color: c.textPrimary,
      "&:hover": { background: c.hover },
    },
    historyName: { fontSize: 12.5, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    historyMeta: { fontSize: 11, color: c.textMuted, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 },
    historyDot: { width: 6, height: 6, borderRadius: 3, background: c.neutral, flexShrink: 0 },
    backBtn: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      background: "transparent",
      border: "none",
      padding: 0,
      cursor: "pointer",
      color: c.textSecondary,
      fontSize: 12,
      fontFamily: "inherit",
      "& svg": { width: 14, height: 14 },
      "&:hover": { color: c.textPrimary },
    },

    // Tons de estado, por último: com a mesma especificidade, valem sobre as
    // regras de base acima (ícone, estado por escrito, selo, pontos).
    ...Object.fromEntries(TONES.map((t) => [`tone_${t}`, { color: c[t] }])),
    ...Object.fromEntries(TONES.map((t) => [`dot_${t}`, { background: c[t] }])),
    ...Object.fromEntries(
      TONES.filter((t) => t !== "neutral").map((t) => [
        `pill_${t}`,
        { color: c[t], background: tint(c[t], dark ? 0.14 : 0.08), borderColor: tint(c[t], dark ? 0.4 : 0.3) },
      ])
    ),
  };
});

export default useTriageStyles;
